'use client'

import type { NavigationFoldoutColumnProps } from '@maestro/commerce-blocks/components/navigation-header/navigation-foldout.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React from 'react'

import { useMobileNavigation } from '@maestro/commerce-blocks/providers/mobile-navigation/context'
import { Button } from '@pearlconvert/ui/components/button'
import { Link } from '@pearlconvert/ui/components/link'

export default function NavigationFoldoutColumn(
  props: NavigationFoldoutColumnProps
) {
  const {
    column,
    index,
    activeNodeIds,
    isExpandable,
    openColumn,
    handleNavigate,
  } = props
  const t = useTranslations('commerce-blocks')
  const { closeMenu, closeLevel } = useMobileNavigation()

  const { parent, items } = column
  const isRoot = index === 0
  const columnTitle = isRoot
    ? t('navigation-header.menu')
    : (parent?.title ?? '')
  const activeChildKey = activeNodeIds[index + 1]

  return (
    <div className="nav-foldout__column" aria-label={columnTitle}>
      <div className="nav-foldout__column-header">
        {!isRoot && (
          <button
            className="nav-foldout__back mobile-only"
            onClick={closeLevel}
            title={t('navigation-header.previous-level')}
            aria-label={t('navigation-header.back')}
          />
        )}
        <span aria-hidden="true" className="nav-foldout__column-title">
          {columnTitle}
        </span>
        <Button
          className="nav-foldout__close"
          onClick={closeMenu}
          title={t('navigation-header.close-menu')}
          type="empty"
          iconLeft="close"
          size="fixed"
        />
      </div>
      <ul className="nav-foldout__list">
        {parent && (
          <li className="nav-foldout__item nav-foldout__item--all">
            <Link
              className="nav-foldout__link nav-foldout__link--all"
              href={parent.url}
              target={parent.target}
              onClick={() => handleNavigate(index, parent.title)}
            >
              {t('navigation-header.aria-all-in', { title: parent.title })}
            </Link>
          </li>
        )}
        {items.map((item) => {
          const expandable = isExpandable(index, Boolean(item.children?.length))
          const isActive = activeChildKey === item._key

          return (
            <li className="nav-foldout__item" key={item._key}>
              {expandable ? (
                <button
                  className={classNames('nav-foldout__button', item.cssClass, {
                    'nav-foldout__button--active': isActive,
                    'nav-foldout__button--highlight': item.highlight,
                  })}
                  aria-expanded={isActive}
                  onClick={() => openColumn(index, item._key)}
                >
                  {item.title}
                </button>
              ) : (
                <Link
                  className={classNames('nav-foldout__link', item.cssClass, {
                    'nav-foldout__link--highlight': item.highlight,
                  })}
                  href={item.url}
                  target={item.target}
                  onClick={() => handleNavigate(index, item.title)}
                >
                  {item.title}
                </Link>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
